/**
 * RsExtractionCollectionPage
 *
 * A single Routine Surveillance Extraction collection, reached from under the
 * Parasite Incidence (API) topic in the sidebar. The collection's articles are
 * topic-restricted to API by the shared context (based on the collection's
 * type). Read-only: no management action bar.
 */

import { useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import JournalArticlesShell, {
  redirectAwayFromForbiddenCollection,
} from "@/components/journal-articles/JournalArticlesShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import {
  useJournalArticlesPage,
  stripLocationColumns,
} from "@/components/journal-articles/useJournalArticlesPage"
import { useResolvedCollection } from "@/components/journal-articles/useResolvedCollection"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { Card, CardContent } from "@/components/ui/card"
import { SidebarOption } from "@/components/journal-articles/JournalArticlesSidebar"
import { ROUTINE_SURVEILLANCE_TYPE } from "@/services/biblioService"

/** Title shown while the collection is resolving. */
const LOADING_TITLE: string = "Routine Surveillance Extraction Collection"

/** Short description shown under the collection name heading. */
const PAGE_DESCRIPTION: string = "Browse journal articles that have been extracted for routine surveillance"

/**
 * The Routine Surveillance Extraction collection page.
 *
 * @returns The rendered page element.
 */
export default function RsExtractionCollectionPage(): React.JSX.Element {
  const navigate = useNavigate()
  const resolved = useResolvedCollection(ROUTINE_SURVEILLANCE_TYPE)

  useEffect(() => {
    if (resolved.status === "not-found") {
      redirectAwayFromForbiddenCollection(navigate)
    }
  }, [resolved.status, navigate])

  if (resolved.status === "ready") {
    return (
      <RsExtractionCollectionReady
        collectionId={resolved.collection.id}
        collectionName={resolved.collection.collectionName}
      />
    )
  }

  return (
    <JournalArticlesShell title={LOADING_TITLE}>
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
        <CardContent className="flex flex-1 flex-col min-h-0">
          <LoadingSpinner variant="outline" loadingContent="Loading collection..." />
        </CardContent>
      </Card>
    </JournalArticlesShell>
  )
}

/**
 * Props for the ready-state inner component.
 */
interface RsExtractionCollectionReadyProps {
  /** The resolved collection id. */
  collectionId: number
  /** The resolved collection name. */
  collectionName: string
}

/**
 * The ready-state body: a standard table page scoped to the resolved
 * collection. Separated so the hook calls only run when the collection is
 * resolved (avoiding conditional hook issues).
 *
 * @param props - The resolved collection props.
 * @returns The rendered table page element.
 */
function RsExtractionCollectionReady({
  collectionId,
  collectionName,
}: RsExtractionCollectionReadyProps): React.JSX.Element {
  const sidebarOption: SidebarOption = `collection-${collectionId}`
  const page = useJournalArticlesPage({ sidebarOption })

  const filterOptions = useMemo(
    () => buildCollectionFilterOptions(page.collections),
    [page.collections]
  )

  const visibleColumns = useMemo(
    () => stripLocationColumns(page.visibleColumns),
    [page.visibleColumns]
  )

  return (
    <JournalArticlesShell
      title={collectionName}
      description={PAGE_DESCRIPTION}
      showDetailsPanel
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
    >
      <JournalArticlesTable page={page} filterOptions={filterOptions} visibleColumns={visibleColumns} />
    </JournalArticlesShell>
  )
}
